import React, { useState, useEffect, useContext, useId, useRef } from "react";
import moment from "moment";
import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  Autocomplete,
  TextField,
  Radio,
  RadioGroup,
  FormLabel,
  Typography,
} from "@mui/material";
import { DataContext } from "../../../../context/DataContext";
import { getToken } from "../../../../api/Account";
import { getRuta, simpleMessage } from "../../../../helpers/Helpers";
import { formatearMonto } from "../../../../helpers/formato";
import { fechaDeCredito } from "../../../credits/comunes";
import {
  getClientListAsync,
  getCreditsByClientAsync,
} from "../../../../api/Clients";
import {
  BotonGenerar,
  FormularioSelector,
} from "../../../../components/reportes/selector";
import { MAX_OPCIONES, buscarClientes, filtrarClientes } from "./filtrarClientes";

const SelectorAccountStatus = () => {
  const id = useId();
  const [clientList, setClientList] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);

  // null mientras no se ha elegido cliente o se están cargando sus créditos.
  const [creditList, setCreditList] = useState(null);
  const [selectedCredit, setSelectedCredit] = useState(0);
  const [intentado, setIntentado] = useState(false);
  const [escrito, setEscrito] = useState("");
  // Cliente cuya lista de créditos se espera: una respuesta de un cliente anterior se descarta.
  const clientePedido = useRef(null);

  const { setIsLoading, setIsUnautorized } = useContext(DataContext);
  let ruta = getRuta();
  const token = getToken();

  const avisarError = (result) => {
    if (result.error === "Unauthorized") {
      setIsUnautorized(true);
      return;
    }
    simpleMessage(result.error || "No se pudo conectar con el servidor", "error");
  };

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      try {
        const result = await getClientListAsync(token);
        setIsLoading(false);
        if (!result.statusResponse) {
          avisarError(result);
          return;
        }
        setClientList(result.data.result);
      } catch (e) {
        setIsLoading(false);
        avisarError({ error: "No se pudo conectar con el servidor" });
      }
    })();
  }, []);

  const verReport = () => {
    setIntentado(true);
    if (!selectedClient || selectedCredit === 0) return;
    window.open(`${ruta}/r-accountstatus/${selectedCredit}`);
  };

  const getCreditsByClient = async (client) => {
    setSelectedClient(client);
    setSelectedCredit(0);
    setCreditList(null);
    setIntentado(false);
    clientePedido.current = client ? client.id : null;
    if (client === null) return;

    setIsLoading(true);
    try {
      const result = await getCreditsByClientAsync(token, client.id);
      setIsLoading(false);
      if (clientePedido.current !== client.id) return;
      if (!result.statusResponse) {
        // La lista queda sin mostrar: "no tiene créditos" sería falso.
        avisarError(result);
        return;
      }
      // Del más reciente al más antiguo; si solo hay uno, queda elegido.
      const creditos = [...result.data.result].sort(
        (a, b) => moment(b.fechaEntrega).valueOf() - moment(a.fechaEntrega).valueOf() || b.id - a.id
      );
      setCreditList(creditos);
      if (creditos.length === 1) setSelectedCredit(creditos[0].id);
    } catch (e) {
      setIsLoading(false);
      if (clientePedido.current !== client.id) return;
      avisarError({ error: "No se pudo conectar con el servidor" });
    }
  };

  const errorCliente = intentado && !selectedClient ? "Elija un cliente" : "";
  // Si hay más coincidencias de las que muestra la lista, se pide escribir más.
  const coincidencias = escrito.trim() ? buscarClientes(clientList, escrito).length : 0;
  const ayudaCliente =
    coincidencias > MAX_OPCIONES
      ? `Hay ${coincidencias} clientes con ese nombre y se muestran ${MAX_OPCIONES}: escriba más`
      : "Escriba nombres o apellidos, en cualquier orden";
  const errorCredito =
    intentado && selectedClient && creditList?.length > 0 && selectedCredit === 0
      ? "Elija el crédito del estado de cuenta"
      : "";

  let creditos = null;
  if (selectedClient && creditList) {
    creditos =
      creditList.length === 0 ? (
        <Typography color="text.secondary" textAlign="center">
          Este cliente no tiene créditos entregados.
        </Typography>
      ) : (
        <FormControl error={Boolean(errorCredito)}>
          <FormLabel id={`${id}-creditos`}>Crédito</FormLabel>
          <RadioGroup
            aria-labelledby={`${id}-creditos`}
            name={`${id}-creditos`}
            value={selectedCredit}
            onChange={(event) => setSelectedCredit(Number(event.target.value))}
          >
            {creditList.map((item) => (
              <FormControlLabel
                key={item.id}
                value={item.id}
                control={<Radio />}
                label={`#${item.id} · ${formatearMonto(item.montoEntregado)} · entregado el ${fechaDeCredito(
                  item.fechaEntrega
                )} · ${item.isCanceled ? "Cancelado" : "Vigente"}`}
              />
            ))}
          </RadioGroup>
          {errorCredito ? <FormHelperText>{errorCredito}</FormHelperText> : null}
        </FormControl>
      );
  }

  return (
    <FormularioSelector>
      <Autocomplete
        options={clientList}
        getOptionLabel={(option) => option.fullName}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        filterOptions={(opciones, { inputValue }) => filtrarClientes(opciones, inputValue)}
        // Con la clave por id, dos clientes con el mismo nombre no chocan.
        renderOption={(props, option) => (
          <li {...props} key={option.id}>
            {option.fullName}
          </li>
        )}
        id={`${id}-cliente`}
        value={selectedClient}
        onInputChange={(event, valor) => setEscrito(valor)}
        onChange={(event, newValue) => {
          getCreditsByClient(newValue);
        }}
        noOptionsText="No existe cliente con ese nombre"
        renderInput={(params) => (
          <TextField
            fullWidth
            {...params}
            label="Cliente"
            error={Boolean(errorCliente)}
            helperText={errorCliente || ayudaCliente}
          />
        )}
      />

      {creditos}

      <BotonGenerar onClick={verReport} />
    </FormularioSelector>
  );
};

export default SelectorAccountStatus;
